import { useEffect, useRef, useState } from "react";
import { Loader2, MessageCircleQuestion, Send, X } from "lucide-react";
import api, { API_URL } from "@/lib/api";

const SUGGESTIONS = [
  "How do I record a training session?",
  "How do I send a report to the Co-ordinator?",
  "How do shift sign-in and GPS work?",
];

export default function HelpChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const sessionRef = useRef(null);

  if (!sessionRef.current) {
    let sid = null;
    try {
      sid = localStorage.getItem("dtt_help_session");
      if (!sid) {
        sid = crypto.randomUUID();
        localStorage.setItem("dtt_help_session", sid);
      }
    } catch {
      sid = crypto.randomUUID();
    }
    sessionRef.current = sid;
  }

  useEffect(() => {
    if (open && sessionRef.current) {
      api.get(`/ai/help-chat/history?session_id=${sessionRef.current}`)
        .then((r) => setMessages(r.data.items))
        .catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const patchLastAssistant = (fn) =>
    setMessages((m) => {
      const copy = [...m];
      const i = copy.length - 1;
      if (i >= 0 && copy[i].role === "assistant") copy[i] = fn(copy[i]);
      return copy;
    });

  const send = async (preset) => {
    const message = (preset ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${API_URL}/ai/help-chat`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message }),
      });
      if (!res.ok || !res.body) throw new Error("unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split("\n\n");
        buffer = events.pop() || "";
        for (const evt of events) {
          const line = evt.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const parsed = JSON.parse(payload);
            if (parsed.t) patchLastAssistant((last) => ({ ...last, content: last.content + parsed.t }));
          } catch { /* ignore partial event */ }
        }
      }
    } catch {
      patchLastAssistant((last) => last.content ? last : {
        ...last,
        content: "The assistant is unavailable right now — please try again, or email the office at info@deluxehealthcare.co.uk.",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid="help-chat-open"
        onClick={() => setOpen((v) => !v)}
        aria-label="Open help assistant"
        className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex h-12 w-12 items-center justify-center rounded-full bg-primary text-white shadow-lg hover:bg-brand-hover transition-colors duration-200"
      >
        <MessageCircleQuestion className="h-6 w-6" />
      </button>

      {open && (
        <div
          data-testid="help-chat-panel"
          className="fixed bottom-20 right-4 sm:bottom-24 sm:right-6 z-50 flex w-[92vw] max-w-sm flex-col overflow-hidden rounded-xl border border-border bg-white shadow-2xl"
          style={{ height: "28rem" }}
        >
          <div className="flex items-center justify-between border-b border-border bg-brand-navy px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-white">Help assistant</p>
              <p className="text-[10px] text-slate-300">Answers how-to questions about this system — no access to any records</p>
            </div>
            <button type="button" data-testid="help-chat-close" onClick={() => setOpen(false)} aria-label="Close help assistant"
              className="rounded-md p-1.5 text-slate-300 hover:text-white hover:bg-white/10 transition-colors duration-150">
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-3" data-testid="help-chat-messages">
            {messages.length === 0 && (
              <div className="space-y-2 pt-2">
                <p className="text-xs text-slate-500 text-center">Ask a how-to question, or try one of these:</p>
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" data-testid={`help-chat-suggestion-${s.slice(0, 20).replace(/\W+/g, "-").toLowerCase()}`}
                    onClick={() => send(s)} disabled={busy}
                    className="block w-full rounded-lg border border-border bg-slate-50 px-3 py-2 text-left text-xs text-slate-700 hover:border-primary/50 hover:bg-primary/5 transition-colors duration-150">
                    {s}
                  </button>
                ))}
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`help-chat-message-${i}`}
                className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user" ? "ml-auto bg-primary text-white" : "bg-slate-100 text-slate-800"
                }`}>
                {m.content || (busy && i === messages.length - 1 ? <Loader2 className="h-4 w-4 animate-spin" /> : "")}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <form
            className="flex items-center gap-2 border-t border-border p-2"
            onSubmit={(e) => { e.preventDefault(); send(); }}
          >
            <input
              data-testid="help-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask how to do something…"
              maxLength={1000}
              className="h-10 flex-1 rounded-md border border-border bg-white px-3 text-sm outline-none focus:border-primary"
            />
            <button type="submit" data-testid="help-chat-send" disabled={busy || !input.trim()} aria-label="Send"
              className="flex h-10 w-10 items-center justify-center rounded-md bg-primary text-white hover:bg-brand-hover disabled:opacity-50 transition-colors duration-150">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
